import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useInterview } from '@/contexts/InterviewContext'
import { Mic, MicOff, Send, ArrowLeft } from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

const formSchema = z.object({
  answer: z.string().min(1, { message: 'Cevap boş olamaz' }),
})

const VerbalInterview = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { questions, currentQuestionIndex, setCurrentQuestionIndex, answers, setAnswers } =
    useInterview()

  const [isRecording, setIsRecording] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      answer: '',
    },
  })

  useEffect(() => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      const recognitionInstance = new SpeechRecognition()
      recognitionInstance.continuous = true
      recognitionInstance.interimResults = true
      recognitionInstance.lang = 'tr-TR'

      recognitionInstance.onresult = (event) => {
        let interimTranscript = ''
        let finalTranscript = ''

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript
          } else {
            interimTranscript += event.results[i][0].transcript
          }
        }

        setTranscript(finalTranscript || interimTranscript)
        form.setValue('answer', finalTranscript || interimTranscript)
      }

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error', event.error)
        setIsRecording(false)
        toast({
          title: 'Ses Tanıma Hatası',
          description: `Hata: ${event.error}`,
          variant: 'destructive',
        })
      }

      setRecognition(recognitionInstance)
    } else {
      toast({
        title: 'Tarayıcı Desteği Yok',
        description: 'Tarayıcınız ses tanıma özelliğini desteklemiyor.',
        variant: 'destructive',
      })
    }

    return () => {
      if (recognition) {
        recognition.stop()
      }
    }
  }, [])

  const toggleRecording = () => {
    if (!recognition) return

    if (isRecording) {
      recognition.stop()
      setIsRecording(false)
    } else {
      recognition.start()
      setIsRecording(true)
      toast({
        title: 'Ses Kaydı Başladı',
        description: 'Konuşmanız metne dönüştürülüyor...',
      })
    }
  }

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    if (recognition && isRecording) {
      recognition.stop()
      setIsRecording(false)
    }

    const newAnswers = [...answers]
    newAnswers[currentQuestionIndex] = values.answer
    setAnswers(newAnswers)

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
      form.reset()
      setTranscript('')
    } else {
      setShowConfirmDialog(true)
    }
  }

  const handleFinishInterview = () => {
    toast({
      title: 'Mülakat Tamamlandı',
      description: 'Cevaplarınız kaydedildi.',
    })
    navigate('/')
  }

  const currentQuestion = questions[currentQuestionIndex]

  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Hata</CardTitle>
              <CardDescription>Soru bulunamadı.</CardDescription>
            </CardHeader>
            <CardFooter>
              <Button onClick={() => navigate('/')}>Ana Sayfaya Dön</Button>
            </CardFooter>
          </Card>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate('/interview')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Geri Dön
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-[1fr_400px]">
          <Card className="shadow-md">
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                <span>Sözlü Mülakat</span>
                <span className="text-sm font-normal bg-gray-100 px-3 py-1 rounded-full">
                  Soru {currentQuestionIndex + 1}/{questions.length}
                </span>
              </CardTitle>
              <CardDescription>
                Soruyu sesli olarak cevaplayın veya yazılı olarak girin
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-md border">
                <h3 className="font-medium mb-2">Soru:</h3>
                <p>{currentQuestion.text}</p>
              </div>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="answer"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cevabınız</FormLabel>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant={isRecording ? 'destructive' : 'outline'}
                            size="icon"
                            onClick={toggleRecording}
                          >
                            {isRecording ? <MicOff /> : <Mic />}
                          </Button>
                          <FormControl>
                            <Input
                              placeholder="Cevabınızı buraya yazın veya sesli olarak kaydedin..."
                              {...field}
                              value={transcript || field.value}
                              onChange={(e) => {
                                field.onChange(e)
                                setTranscript(e.target.value)
                              }}
                              className="flex-1"
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full">
                    <Send className="mr-2 h-4 w-4" />
                    {currentQuestionIndex < questions.length - 1
                      ? 'Sonraki Soru'
                      : 'Mülakatı Tamamla'}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>İpuçları</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-medium mb-1">Ses Kaydı</h3>
                  <p className="text-sm text-gray-600">
                    Mikrofon butonuna tıklayarak ses kaydını başlatabilirsiniz. Konuşmanız otomatik
                    olarak metne dönüştürülecektir.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium mb-1">Yazılı Cevap</h3>
                  <p className="text-sm text-gray-600">
                    Cevabınızı doğrudan metin kutusuna yazabilirsiniz.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>İlerleme</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-primary h-2.5 rounded-full"
                    style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                  ></div>
                </div>
                <p className="text-center mt-2 text-sm text-gray-600">
                  {currentQuestionIndex + 1} / {questions.length} soru
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mülakatı Tamamla</DialogTitle>
            <DialogDescription>
              Tüm soruları cevapladınız. Mülakatı tamamlamak istiyor musunuz?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Geri Dön
            </Button>
            <Button onClick={handleFinishInterview}>Mülakatı Tamamla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  )
}

export default VerbalInterview
